import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ShoppingCart, Shield, Clock, Gift, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import Seo from '@/components/Seo';
import { TierCelebration } from '@/components/TierCelebration';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const CATEGORY_EMOJI = {
  mains: '🍛', light_meals: '🍔', pasta: '🍝', salads: '🥗', sandwiches: '🥪',
  office_platters: '🍱', platters: '🍱', starters: '🥟', desserts: '🍮',
  beverages: '🥤', soft_drinks: '🥤', energy_drinks: '🥤', juice: '🧃', water: '💧',
  chips: '🍟', snacks: '🍟', chocolates: '🍫', sweets: '🍬'
};
const normCat = (c) => (c || '').toString().trim().toLowerCase().replace(/\s+/g, '_');
const titleCase = (c) => c.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, ch => ch.toUpperCase());

export default function AppHome() {
  const { user, token } = useAuth();
  const { addItem, addMultipleItems, openCart, itemCount } = useCart();
  const navigate = useNavigate();
  const [specials, setSpecials] = useState([]);
  const [popular, setPopular] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loyalty, setLoyalty] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);
  const [voucherStats, setVoucherStats] = useState(null);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/menu/todays-specials`).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/menu/popular?limit=6`).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/menu/categories`).then(r => r.ok ? r.json() : [])
    ])
      .then(([specialsData, popularData, cats]) => {
        setSpecials(Array.isArray(specialsData) ? specialsData : []);
        setPopular(Array.isArray(popularData) ? popularData : []);
        const rawCats = Array.isArray(cats) ? cats : [];
        setCategories([...new Map(rawCats.map(c => [normCat(c), c])).values()]);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/loyalty/status`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
    }).then(r => r.ok ? r.json() : null).then(d => {
      setLoyalty(d);
      if (d?.celebration_pending) setShowCelebration(true);
    }).catch(() => {});
    fetch(`${API_URL}/api/orders`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
    }).then(r => r.ok ? r.json() : []).then(d => {
      const sorted = (d || []).filter(o => (o.items || []).length > 0)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setRecentOrders(sorted.slice(0, 5));
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    fetch(`${API_URL}/api/vouchers/stats`).then(r => r.ok ? r.json() : null).then(setVoucherStats).catch(() => {});
  }, []);

  const dismissCelebration = () => {
    setShowCelebration(false);
    fetch(`${API_URL}/api/loyalty/celebration-ack`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
    }).catch(() => {});
  };

  const handleReorder = async (orderId) => {
    if (!token) return;
    setReordering(true);
    try {
      const r = await fetch(`${API_URL}/api/orders/${orderId}/reorder`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed to reorder');
      if (!d.items || d.items.length === 0) { toast.error('Those items are no longer available'); return; }
      addMultipleItems(d.items);
      toast.success(`${d.items.length} item${d.items.length > 1 ? 's' : ''} added to your cart!`);
      openCart();
    } catch (e) { toast.error(e.message); }
    finally { setReordering(false); }
  };

  const handleAddToCart = (item) => {
    addItem({
      item_id: item.item_id,
      name: item.name,
      price: item.price,
      image_url: item.image_url
    });
    toast.success('Added to cart', { duration: 1500 });
  };

  // Today's specials rail (BOH specials & featured first) + most-ordered meals
  const featuredItems = (specials.length > 0 ? specials : popular).slice(0, 6);
  const popularItems = (popular.length > 0 ? popular : specials).slice(0, 4);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-background"
      data-testid="app-home"
    >
      {showCelebration && loyalty && <TierCelebration loyalty={loyalty} onClose={dismissCelebration} />}
      <Seo title="Home" path="/" description="Welcome to GC3H Online Canteen — order fresh, chef-prepared meals for pickup or delivery. See today's specials, the meal of the day and the most-ordered dishes, then check out securely in seconds." />
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-border">
        <div className="px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <motion.p 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-muted-foreground"
              >
                {user ? `Welcome back,` : 'Welcome to'}
              </motion.p>
              <motion.h1 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="font-heading text-2xl font-semibold text-secondary-900"
              >
                {user ? user.name?.split(' ')[0] : 'GC3H'}
              </motion.h1>
            </div>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={openCart}
              className="relative p-3 bg-primary/10 rounded-2xl"
              data-testid="cart-btn"
            >
              <ShoppingCart className="h-6 w-6 text-primary" />
              {itemCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center"
                >
                  {itemCount}
                </motion.span>
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Hero Card */}
      <div className="px-5 py-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative h-48 rounded-3xl overflow-hidden"
          data-testid="hero-card"
        >
          <img 
            src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800"
            alt="Delicious food"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary-900/80 to-transparent" />
          <div className="absolute inset-0 p-6 flex flex-col justify-end">
            <h2 className="text-white text-2xl font-heading font-semibold mb-2">
              Order Smart. Eat Fresh.
            </h2>
            <p className="text-white/80 text-sm">
              Certified Halaal catering delivered to you
            </p>
          </div>
        </motion.div>
      </div>

      {/* Loyalty progress */}
      {loyalty && (
        <div className="px-5 pt-2">
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => navigate('/menu')}
            className={`w-full text-left rounded-3xl p-4 border ${loyalty.milestone_reached ? 'bg-gradient-to-r from-amber-400 to-amber-500 border-amber-300' : 'bg-white border-border'}`}
            data-testid="loyalty-card"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{loyalty.tier_emoji}</span>
                <div>
                  <p className={`text-sm font-semibold ${loyalty.milestone_reached ? 'text-white' : 'text-secondary-900'}`}>
                    {loyalty.tier_name} {loyalty.current_tier > 0 ? `· Tier ${loyalty.current_tier}` : 'Member'}
                  </p>
                  <p className={`text-xs ${loyalty.milestone_reached ? 'text-white/90' : 'text-muted-foreground'}`}>
                    {loyalty.lifetime_orders} lifetime orders
                  </p>
                </div>
              </div>
              {loyalty.milestone_reached ? (
                <span className="flex items-center gap-1 bg-white/90 text-amber-600 text-xs font-bold px-2.5 py-1 rounded-full">
                  <Gift className="h-3.5 w-3.5" /> {loyalty.discount_percent}% reward ready
                </span>
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            {loyalty.next_tier_name ? (
              <>
                <div className={`h-2 rounded-full overflow-hidden ${loyalty.milestone_reached ? 'bg-white/30' : 'bg-muted'}`}>
                  <div className={`h-full rounded-full ${loyalty.milestone_reached ? 'bg-white' : 'bg-primary'}`} style={{ width: `${loyalty.progress_percent}%` }} />
                </div>
                <p className={`text-xs mt-1.5 ${loyalty.milestone_reached ? 'text-white/90' : 'text-muted-foreground'}`}>
                  {loyalty.orders_to_next} more order{loyalty.orders_to_next === 1 ? '' : 's'} to {loyalty.next_tier_emoji} {loyalty.next_tier_name} ({loyalty.next_tier_discount}% off)
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">You've reached the highest tier — enjoy your rewards! 🌟</p>
            )}
            {loyalty.milestone_reached && (
              <div className="mt-2.5 flex items-center gap-2 bg-white/25 rounded-xl px-3 py-2" data-testid="reward-ready-banner">
                <Gift className="h-4 w-4 text-white shrink-0" />
                <p className="text-xs font-semibold text-white">
                  Reward ready — {loyalty.discount_percent}% off will apply automatically to your next order
                </p>
              </div>
            )}
          </motion.button>
        </div>
      )}

      {/* Rewards guide link */}
      <div className="px-5 pt-2">
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/rewards-guide')}
          className="w-full text-left rounded-3xl p-4 bg-white dark:bg-slate-800 border border-border flex items-center gap-3 shadow-sm"
          data-testid="rewards-guide-banner"
        >
          <div className="h-10 w-10 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
            <Gift className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-secondary-900 dark:text-white">How rewards work</p>
            <p className="text-xs text-muted-foreground">Loyalty tiers, referrals &amp; free lunches — the full guide</p>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </motion.button>
      </div>

      {/* Gift a voucher promo banner */}
      <div className="px-5 pt-2">
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/profile?action=buy-voucher')}
          className="w-full text-left rounded-3xl p-4 bg-gradient-to-r from-rose-500 to-primary flex items-center gap-4 shadow-md"
          data-testid="gift-voucher-banner"
        >
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Gift className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold text-sm">Gift a voucher 🎁</p>
            <p className="text-white/85 text-xs">
              {voucherStats?.gifted_this_month > 0
                ? `${voucherStats.gifted_this_month} ${voucherStats.gifted_this_month === 1 ? 'voucher' : 'vouchers'} gifted this month — send one in seconds`
                : 'Treat a colleague or friend — send a gift card in seconds'}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-white/90" />
        </motion.button>
      </div>

      {/* Order again — recent orders */}
      {recentOrders.length > 0 && (
        <div className="px-5 pt-4" data-testid="order-again-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-semibold text-secondary-900">Order again</h3>
            <button onClick={() => navigate('/orders')} className="text-primary text-sm font-medium flex items-center gap-1">
              All orders <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {recentOrders.map((order) => {
              const first = order.items[0];
              const extra = order.items.length - 1;
              return (
                <div
                  key={order.order_id}
                  className="flex-shrink-0 w-60 bg-white border border-border rounded-2xl p-3 flex items-center gap-3"
                  data-testid={`reorder-card-${order.order_id}`}
                >
                  <img
                    src={first?.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200'}
                    alt={first?.name}
                    className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-secondary-900 text-sm truncate">{first?.name}</p>
                    <p className="text-xs text-muted-foreground">{extra > 0 ? `+${extra} more · ` : ''}R{order.total_amount?.toFixed(2)}</p>
                  </div>
                  <button
                    onClick={() => handleReorder(order.order_id)}
                    disabled={reordering}
                    className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 disabled:opacity-50"
                    data-testid={`reorder-btn-${order.order_id}`}
                    aria-label="Reorder"
                  >
                    <RefreshCcw className={`h-4 w-4 text-primary ${reordering ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="px-5 py-2">
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
          {[
            { icon: Shield, label: 'Halaal Certified', color: 'bg-green-500' },
            { icon: Clock, label: 'Next Day Delivery', color: 'bg-blue-500' },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex-shrink-0 flex items-center gap-2 bg-white border border-border rounded-2xl px-4 py-3"
            >
              <div className={`${stat.color} p-1.5 rounded-lg`}>
                <stat.icon className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm font-medium text-secondary-900 whitespace-nowrap">{stat.label}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-secondary-900">Categories</h3>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-24 h-24 bg-muted rounded-2xl animate-pulse" />
            ))
          ) : (
            categories.map((cat, idx) => (
              <motion.button
                key={cat}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(`/menu?category=${encodeURIComponent(cat)}`)}
                className="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl flex flex-col items-center justify-center gap-2 border border-primary/20"
                data-testid={`category-${cat}`}
              >
                <span className="text-2xl">
                  {CATEGORY_EMOJI[normCat(cat)] || '🍽️'}
                </span>
                <span className="text-xs font-medium text-secondary-900 text-center px-2">{titleCase(cat)}</span>
              </motion.button>
            ))
          )}
        </div>
      </div>

      {/* Today's Specials */}
      {(loading || featuredItems.length > 0) && (
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-secondary-900">Today&apos;s Specials</h3>
          <button 
            onClick={() => navigate('/menu')}
            className="text-primary text-sm font-medium flex items-center gap-1"
          >
            View All <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1 snap-x snap-mandatory">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-64 h-72 bg-muted rounded-3xl animate-pulse" />
            ))
          ) : (
            featuredItems.map((item, idx) => (
              <motion.div
                key={item.item_id}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="flex-shrink-0 w-64 snap-center"
                data-testid={`special-${item.item_id}`}
              >
                <div className="bg-white rounded-3xl overflow-hidden border border-border shadow-sm">
                  <div className="relative h-40">
                    <img 
                      src={item.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                    {item.is_vegetarian && (
                      <span className="absolute top-3 left-3 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                        Veg
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-secondary-900 mb-1 truncate">{item.name}</h4>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{item.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-primary">R{item.price.toFixed(2)}</span>
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleAddToCart(item)}
                        className="bg-primary text-white px-4 py-2 rounded-xl text-sm font-medium"
                        data-testid={`add-${item.item_id}`}
                      >
                        Add
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
      )}

      {/* Popular Meals — ranked by real order volume */}
      {(loading || popularItems.length > 0) && (
      <div className="px-5 py-4 pb-24">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-secondary-900">Most Ordered Meals</h3>
        </div>
        <div className="space-y-3">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded-2xl animate-pulse" />
            ))
          ) : (
            popularItems.map((item, idx) => (
              <motion.div
                key={item.item_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center gap-4 bg-white p-3 rounded-2xl border border-border"
                data-testid={`popular-${item.item_id}`}
              >
                <img 
                  src={item.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200'}
                  alt={item.name}
                  className="w-20 h-20 rounded-xl object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-secondary-900 truncate">{item.name}</h4>
                  <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
                  <span className="text-primary font-bold">R{item.price.toFixed(2)}</span>
                </div>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleAddToCart(item)}
                  className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center"
                >
                  <span className="text-primary text-xl">+</span>
                </motion.button>
              </motion.div>
            ))
          )}
        </div>
      </div>
      )}

      {/* SEO / informational content — who we are, where we deliver and how ordering works */}
      <section className="px-5 pt-10 pb-28 max-w-xl mx-auto w-full text-sm text-muted-foreground space-y-3" data-testid="home-seo-content">
        <h2 className="text-base font-semibold text-secondary-900 dark:text-white">Fresh, affordable lunch — ordered online, ready when you are</h2>
        <p>
          GC3H Online Canteen is the online ordering platform of the Gallagher Convention Centre canteen in Midrand,
          Gauteng. Every weekday our kitchen prepares a fresh menu of chef-cooked mains, light meals, toasted sandwiches
          and salads, alongside a fully stocked canteen shop of cold drinks, chips, chocolates and snacks. Order from your
          phone or desktop before the 9AM cut-off and your food is cooked the same day — no queues, no cash, no fuss.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">Lunch delivery across Midrand and surrounds</h3>
        <p>
          Collect free from the canteen at Gallagher Convention Centre, or have your order delivered to your office or
          site. We deliver daily to businesses, warehouses and industrial parks around Midrand, Waterfall, Kyalami and
          Sunninghill, with affordable flat delivery fees and free delivery on qualifying orders. Teams ordering together
          can also arrange office platters for meetings, workshops and corporate events.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">More than just a lunch order</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Digital wallet</strong> — top up once and pay for every order with a single tap.</li>
          <li><strong>Loyalty rewards</strong> — earn tier discounts automatically the more meals you order.</li>
          <li><strong>Gift vouchers</strong> — buy branded meal vouchers for colleagues, birthdays and thank-yous.</li>
          <li><strong>Invite &amp; earn</strong> — refer a friend and earn wallet credit toward free lunches.</li>
          <li><strong>Live order tracking</strong> — follow your meal from the kitchen to collection, with WhatsApp and email updates.</li>
        </ul>
        <h3 className="font-semibold text-secondary-900 dark:text-white">How it works</h3>
        <p>
          Browse today's menu and specials, tap the dishes you want, choose your sides, and check out securely with your
          wallet or card. Place your order before 9AM to eat the same day. When your food is ready you'll get a
          notification — show your unique collection QR code at the counter or wait for the driver to arrive. Whether it's
          a quick weekday meal, a vegetarian or halaal-friendly option, or catering for the whole team, GC3H Online
          Canteen keeps lunchtime fresh, fast and hassle-free.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">Frequently asked questions</h3>
        <p>
          <strong>What time do same-day orders close?</strong> Orders for today close at 9:00 AM, with a short ten-minute
          grace period. After that, every new order is scheduled for the next business day — the app always shows you
          exactly which day your food will arrive before you pay.
        </p>
        <p>
          <strong>Is the food fresh?</strong> Yes — nothing is pre-packaged days ahead. The kitchen team shops and cooks
          each weekday morning based on the orders received, which is why the morning cut-off exists. Your lunch is
          prepared the very same day it reaches your desk.
        </p>
        <p>
          <strong>Can my company order for the whole team?</strong> Absolutely. Office platters serve meetings, training
          sessions and events of any size, and the wallet makes it easy for one person to pay for group orders. Get in
          touch through the in-app messages tab for custom catering quotes.
        </p>
        <p>
          <strong>What happens if I need to cancel?</strong> Unpaid orders can be modified or cancelled in the app before
          the cut-off, and any loyalty reward or voucher used is automatically returned to your account so you never lose
          a discount you'd earned.
        </p>
      </section>
    </motion.div>
  );
}
